import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { UserRole, rolePermissions, Permission, Resource, Action } from '../types/rbac';

export interface RBACContextType {
  getPermissions: (role: UserRole) => Permission[];
  hasPermission: (resource: Resource, action: Action) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
  hasAnyPermission: (permissions: { resource: Resource; action: Action }[]) => boolean;
  hasRole: (role: UserRole) => boolean;
  getUserPermissions: () => Permission[];
  canAccess: (resource: Resource, action: Action) => boolean;
}

const RBACContext = createContext<RBACContextType | undefined>(undefined);

export const RBACProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const getPermissions = useCallback((role: UserRole): Permission[] => {
    const rolePermission = rolePermissions.find(rp => rp.role === role);
    return rolePermission ? rolePermission.permissions : [];
  }, []);

  const hasPermission = useCallback((resource: Resource, action: Action): boolean => {
    if (!user) return false;
    const permissions = getPermissions(user.role);
    return permissions.some(p => p.resource === resource && p.action === action);
  }, [user, getPermissions]);

  const hasAnyRole = useCallback((roles: UserRole[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  }, [user]);

  const hasAnyPermission = useCallback((permissions: { resource: Resource; action: Action }[]): boolean => {
    if (!user) return false;
    return permissions.some(p => hasPermission(p.resource, p.action));
  }, [user, hasPermission]);

  const hasRole = useCallback((role: UserRole): boolean => {
    if (!user) return false;
    return user.role === role;
  }, [user]);

  const getUserPermissions = useCallback((): Permission[] => {
    if (!user) return [];
    return getPermissions(user.role);
  }, [user, getPermissions]);

  const canAccess = useCallback((resource: Resource, action: Action): boolean => {
    return hasPermission(resource, action);
  }, [hasPermission]);

  const value = useMemo(() => ({
    getPermissions,
    hasPermission,
    hasAnyRole,
    hasAnyPermission,
    hasRole,
    getUserPermissions,
    canAccess,
  }), [getPermissions, hasPermission, hasAnyRole, hasAnyPermission, hasRole, getUserPermissions, canAccess]);

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
};

export const useRBAC = (): RBACContextType => {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within a RBACProvider');
  }
  return context;
}; 